import React, { useCallback, useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { assignmentService } from '../services/apiService'

const AssignmentDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [assignment, setAssignment] = useState(null)
  const [submission, setSubmission] = useState(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    content: '',
    file: null,
  })

  const checkSubmission = useCallback(async () => {
    try {
      const response = await assignmentService.getMySubmissions()
      const data = response.data.results || response.data
      const submissions = Array.isArray(data) ? data : []
      const existingSubmission = submissions.find(
        (sub) => sub.assignment === parseInt(id, 10),
      )
      if (existingSubmission) {
        setSubmission(existingSubmission)
        setFormData({ content: existingSubmission.content, file: null })
      }
    } catch (error) {
      console.error('Error checking submission:', error)
    }
  }, [id])

  const fetchAssignment = useCallback(async () => {
    try {
      const response = await assignmentService.getAssignment(id)
      setAssignment(response.data)
    } catch (error) {
      console.error('Error fetching assignment:', error)
      alert('Assignment not found')
      navigate('/assignments')
    } finally {
      setLoading(false)
    }
  }, [id, navigate])

  useEffect(() => {
    fetchAssignment()
    checkSubmission()
  }, [fetchAssignment, checkSubmission])

  const handleChange = (e) => {
    if (e.target.name === 'file') {
      setFormData({ ...formData, file: e.target.files[0] })
    } else {
      setFormData({ ...formData, [e.target.name]: e.target.value })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (submission) {
      alert('You have already submitted this assignment')
      return
    }

    setSubmitting(true)
    try {
      const submitData = new FormData()
      submitData.append('content', formData.content)
      if (formData.file) {
        submitData.append('file', formData.file)
      }

      await assignmentService.submitAssignment(id, submitData)
      alert('Assignment submitted successfully!')
      checkSubmission()
    } catch (error) {
      console.error('Error submitting assignment:', error)
      alert('Failed to submit assignment. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const isOverdue = () => {
    return new Date(assignment.due_date) < new Date()
  }

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600'></div>
      </div>
    )
  }

  if (!assignment) {
    return null
  }

  return (
    <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
      <Link
        to='/assignments'
        className='text-primary-600 hover:text-primary-700 mb-4 inline-flex items-center'
      >
        <svg
          className='h-5 w-5 mr-2'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M10 19l-7-7m0 0l7-7m-7 7h18'
          />
        </svg>
        Back to Assignments
      </Link>

      <div className='bg-white rounded-lg shadow-lg p-8 mt-4'>
        <div className='mb-6'>
          <h1 className='text-3xl font-bold text-gray-900 mb-4'>
            {assignment.title}
          </h1>

          <div className='flex items-center space-x-4 text-sm mb-6'>
            <span
              className={`flex items-center ${
                isOverdue() && !submission ? 'text-red-600' : 'text-gray-600'
              }`}
            >
              <svg
                className='h-5 w-5 mr-1'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'
                />
              </svg>
              Due: {new Date(assignment.due_date).toLocaleString()}
              {isOverdue() && !submission && ' (Overdue)'}
            </span>
            <span className='text-gray-600'>
              Max Score: {assignment.max_score} points
            </span>
          </div>

          {submission && (
            <div
              className={`p-4 rounded-md mb-6 ${
                submission.is_graded
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-blue-50 border border-blue-200'
              }`}
            >
              <div className='flex items-center justify-between'>
                <div>
                  <p className='font-medium text-gray-900'>
                    {submission.is_graded ? 'Graded' : 'Submitted'}
                  </p>
                  <p className='text-sm text-gray-600'>
                    Submitted on:{' '}
                    {new Date(submission.submitted_at).toLocaleString()}
                    {submission.is_late && (
                      <span className='text-red-600 ml-2'>(Late)</span>
                    )}
                  </p>
                </div>
                {submission.is_graded && (
                  <div className='text-right'>
                    <p className='text-2xl font-bold text-gray-900'>
                      {submission.score}/{assignment.max_score}
                    </p>
                    <p className='text-sm text-gray-600'>
                      {Math.round(
                        (submission.score / assignment.max_score) * 100,
                      )}
                      %
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className='prose max-w-none mb-8'>
          <h2 className='text-xl font-semibold mb-4'>Instructions</h2>
          <p className='text-gray-700 whitespace-pre-wrap'>
            {assignment.description}
          </p>
        </div>

        {submission ? (
          <div>
            <h2 className='text-xl font-semibold mb-4'>Your Submission</h2>
            <div className='bg-gray-50 p-4 rounded-md mb-4'>
              <p className='text-gray-700 whitespace-pre-wrap'>
                {submission.content}
              </p>
              {submission.file && (
                <div className='mt-4'>
                  <a
                    href={submission.file}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-primary-600 hover:text-primary-700 inline-flex items-center'
                  >
                    <svg
                      className='h-5 w-5 mr-2'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                      />
                    </svg>
                    Download Attached File
                  </a>
                </div>
              )}
            </div>

            {submission.is_graded && submission.feedback && (
              <div className='mt-6'>
                <h3 className='text-lg font-semibold mb-2'>
                  Instructor Feedback
                </h3>
                <div className='bg-yellow-50 border border-yellow-200 rounded-md p-4'>
                  <p className='text-gray-700 whitespace-pre-wrap'>
                    {submission.feedback}
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>
            <h2 className='text-xl font-semibold mb-4'>Submit Assignment</h2>

            {isOverdue() && (
              <div className='bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6'>
                <div className='flex'>
                  <div className='flex-shrink-0'>
                    <svg
                      className='h-5 w-5 text-yellow-400'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        fillRule='evenodd'
                        d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                        clipRule='evenodd'
                      />
                    </svg>
                  </div>
                  <div className='ml-3'>
                    <p className='text-sm text-yellow-700'>
                      This assignment is overdue. Late submissions may receive
                      reduced credit.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className='space-y-6'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  Submission Text *
                </label>
                <textarea
                  name='content'
                  required
                  value={formData.content}
                  onChange={handleChange}
                  rows={10}
                  className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500'
                  placeholder='Enter your assignment solution or response here...'
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  Attach File (Optional)
                </label>
                <input
                  type='file'
                  name='file'
                  onChange={handleChange}
                  className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500'
                />
                <p className='text-sm text-gray-500 mt-1'>
                  Supported formats: PDF, DOC, DOCX, ZIP, images
                </p>
              </div>

              <div className='flex justify-end'>
                <button
                  type='submit'
                  disabled={submitting}
                  className='bg-primary-600 text-white px-6 py-3 rounded-md font-medium hover:bg-primary-700 disabled:opacity-50'
                >
                  {submitting ? 'Submitting...' : 'Submit Assignment'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  )
}

export default AssignmentDetail
